/**
 * Matrix empty-cell scaffold routing (S21/P4) — the pure half of
 * `ViewMatrixContainer`'s write path. An empty (row → col) cell on key axes
 * scaffolds a `row > col` draft step through the WYSIWYG service's
 * `addProcessWithStep` (§3.3 zero-prompt anonymous process — engine
 * `scaffoldProcessEdit`/`scaffoldEdgeEdit` under the hood): the step lands in
 * the FROM (row) module's file, nested into the pair's common ancestor when
 * one exists there. Operand references are minimal unique suffixes (§4.11
 * hoisting mirror), like every other drawEdge path.
 */
import { shortestUniqueSuffix } from "@archlang/engine";

export interface MatrixScaffoldArgs {
  readonly name: null; // zero-prompt anonymous — the matrix has no naming UI
  readonly anchorModuleId: string | null;
  readonly fileOfModuleId: string;
  readonly from: string;
  readonly to: string;
}

/** The `addProcessWithStep` args for scaffolding `rowKey > colKey`, or `null`
 *  for a self-cell (the diagonal never scaffolds). Keys are qualified module
 *  keys — the matrix only offers scaffolding on ungrouped (key) axes. */
export function matrixScaffoldArgs(
  allModulePaths: readonly (readonly string[])[],
  rowKey: string,
  colKey: string,
): MatrixScaffoldArgs | null {
  if (rowKey === colKey) return null;
  const fq = rowKey.split(".");
  const tq = colKey.split(".");
  const from = shortestUniqueSuffix(allModulePaths, fq);
  const to = shortestUniqueSuffix(allModulePaths, tq);
  // Common ancestor = shared qualified-name prefix (§3.3 locality).
  let i = 0;
  while (i < fq.length - 1 && i < tq.length - 1 && fq[i] === tq[i]) i++;
  const anchor = i > 0 ? fq.slice(0, i).join(".") : null;
  return { name: null, anchorModuleId: anchor, fileOfModuleId: rowKey, from, to };
}
